import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Account, AccountSubscriptionStatus } from '../api/client';
import { isOnDemand, subscriptionDaysLeft } from '../utils/subscription';
import { AccountAvatar } from './AccountAvatar';
import { FeatherIcon } from './FeatherIcon';
import { AccountPickerMenu, type PickerAccount } from './AccountPicker';
import { PageTabs, type PageTab } from './PageHeader';
import { C, FONT } from './ChatAssistant/compact/tokens';
import './ChatAssistant/compact/compact.css';

/* Account header (compact redesign, option 1b): identity row with purple-only
   status chips, then the products as underline tabs. A chip is purple while its
   status is on (plan, AI, login) and dark grey while it is off. The Chat assistant
   page shows it without tabs, its avatar and name opening the Account switcher. */

export type AccountHeaderTab = PageTab;

interface AccountHeaderProps {
  account: Account;
  modelName: string | null;
  subStatus: AccountSubscriptionStatus | null;
  /** The underline tabs; none on the Chat assistant page. */
  tabs?: AccountHeaderTab[];
  activeTab?: string;
  onTab?: (id: string) => void;
  onSettings?: () => void;
  /** Who can work on this account (owners and super admins). */
  onAccess?: () => void;
  onFinishSetup?: () => void;
  /** The avatar and name open this Account list (the Chat assistant page). */
  switcher?: { accounts: PickerAccount[]; onChange: (accountId: string) => void };
}

const ASSISTANT_PLAN = 'monthly_core_4_features';
const NO_ASSISTANT_PLAN = 'monthly_core_no_assistant';

function StatusChip({ on, icon, label, tip }: { on: boolean; icon: string; label: string; tip?: string }) {
  return (
    <span
      title={tip}
      style={{
        height: 20, padding: '0 7px', display: 'inline-flex', alignItems: 'center', gap: 5, boxSizing: 'border-box',
        border: `1px solid ${on ? C.tintLine : C.border}`, borderRadius: 99, background: on ? C.tint16 : C.input,
        color: on ? C.purplePale : C.muted, font: `500 11px ${FONT}`, whiteSpace: 'nowrap',
      }}
    >
      <FeatherIcon name={icon} size={11} />
      {label}
    </span>
  );
}

function IconButton({ icon, label, active, onClick }: { icon: string; label: string; active?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="cpt-hover-icon"
      style={{
        width: 30, height: 30, flex: 'none', border: 0, borderRadius: 6, background: 'transparent',
        color: active ? C.purpleLt : C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <FeatherIcon name={icon} size={15} />
    </button>
  );
}

export function AccountHeader({
  account, modelName, subStatus, tabs = [], activeTab = '', onTab = () => {}, onSettings, onAccess, onFinishSetup, switcher,
}: AccountHeaderProps) {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const [switching, setSwitching] = useState(false);
  const name = account.profile_name || account.username || account.account_id;
  const toggleSwitch = () => setSwitching((o) => !o);
  const switchLabel = tc('massPages.chat.switchAccount');
  const avatar = <AccountAvatar avatarUrl={account.avatar_url} platform={account.platform} flagCorner="top-right" size={40} className="shrink-0" />;

  const plan = (() => {
    if (!subStatus) return null;
    // On demand accounts pay per response from the wallet and have no Subscription
    // row, so `active` is false; they are not "No plan".
    if (isOnDemand(subStatus)) {
      const paused = !!subStatus.billing_suspended;
      return paused
        ? { on: false, label: t('accountView.onDemandPaused'), tip: t('accountView.onDemandPausedTip') }
        : { on: true, label: t('accountView.onDemand'), tip: t('accountView.onDemandTip') };
    }
    if (!subStatus.active) return { on: false, label: t('accountView.noPlan'), tip: t('accountView.subscriptionNotPaid') };
    const code = subStatus.plan_code;
    const name = code === ASSISTANT_PLAN ? t('accountView.planAssistant') : code === NO_ASSISTANT_PLAN ? t('accountView.planNoAssistant') : t('accountView.plan');
    const days = subscriptionDaysLeft(subStatus);
    return { on: true, label: days !== null ? t('accountView.daysLeft', { plan: name, days }) : name, tip: undefined };
  })();

  const handle = [account.username ? `@${account.username}` : null, modelName].filter(Boolean).join(' · ');

  return (
    <div className="cpt-root" style={{ marginBottom: 12 }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '0 0 12px' }}>
        {switcher ? (
          <button type="button" onClick={toggleSwitch} title={switchLabel} aria-label={switchLabel} aria-expanded={switching} style={{ padding: 0, border: 0, background: 'transparent', cursor: 'pointer', flex: 'none' }}>
            {avatar}
          </button>
        ) : avatar}
        {switcher && switching && (
          <AccountPickerMenu accounts={switcher.accounts} value={account.account_id} onChange={(id) => id && switcher.onChange(id)} onClose={() => setSwitching(false)} align="left" required top={46} />
        )}
        {/* Grows to push the actions right; on phones the actions wrap to their own line. */}
        <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', rowGap: 4 }}>
            <h1 style={{ margin: 0, font: `700 17px/1.2 ${FONT}`, color: C.text, overflowWrap: 'anywhere' }}>
              {switcher ? (
                <button
                  type="button"
                  onClick={toggleSwitch}
                  title={switchLabel}
                  aria-expanded={switching}
                  className="cpt-hover-icon"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px', margin: '0 -4px', border: 0, borderRadius: 6, background: 'transparent', color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer', overflowWrap: 'anywhere' }}
                >
                  {name}
                  <FeatherIcon name="chevron-down" size={15} />
                </button>
              ) : name}
            </h1>
            {plan && <StatusChip on={plan.on} icon="credit-card" label={plan.label} tip={plan.tip} />}
            <StatusChip
              on={account.is_enabled}
              icon={account.is_enabled ? 'cpu' : 'pause-circle'}
              label={account.is_enabled ? t('accountView.aiEnabled') : t('accountView.aiDisabled')}
            />
            <StatusChip
              on={!!account.is_logged_in}
              icon={account.is_logged_in ? 'log-in' : 'log-out'}
              label={account.is_logged_in ? t('accountView.loggedIn') : t('accountView.loggedOut')}
            />
            {onSettings && <IconButton icon="settings" label={t('accountView.accountSettings')} onClick={onSettings} />}
            {onAccess && <IconButton icon="users" label={t('accountView.tabs.access')} active={activeTab === 'access'} onClick={onAccess} />}
          </div>
          {handle && <span style={{ font: `400 12px ${FONT}`, color: C.muted, overflowWrap: 'anywhere' }}>{handle}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
        {onFinishSetup && (
          <button
            type="button"
            onClick={onFinishSetup}
            title={t('accountView.finishSetupTitle')}
            style={{
              height: 28, flex: 'none', padding: '0 12px', border: 0, borderRadius: 6, background: C.purple, color: '#fff',
              font: `600 12px ${FONT}`, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {t('accountView.finishSetup')}
          </button>
        )}
        </div>
      </div>

      {tabs.length > 0 && <PageTabs tabs={tabs} active={activeTab} onChange={onTab} />}
    </div>
  );
}
